/**
 * Secure Memory Utilities
 *
 * Security-critical utilities for handling sensitive data in memory.
 * Implements recommendations from HAL-03 audit finding.
 */

/**
 * Securely wipes sensitive data from memory.
 *
 * This function attempts to zero out Uint8Array buffers containing sensitive data
 * (private keys, master secrets, etc.) and triggers garbage collection if available.
 *
 * **Security Note**: This is a best-effort approach. JavaScript doesn't guarantee
 * memory clearing due to:
 * - Internal copies created by the JS engine
 * - JIT compiler optimizations
 * - Non-deterministic garbage collection
 * - Browser-specific memory management
 *
 * However, this significantly reduces the attack surface for memory-based attacks
 * (XSS, heap snapshots, memory dumps, malicious extensions, etc.)
 *
 * @param data - One or more Uint8Array buffers to securely wipe from memory
 * @example
 * ```typescript
 * const privateKey = await getPrivateKey();
 * const masterSecret = await getMasterSecret();
 * try {
 *   // Use sensitive data
 *   return result;
 * } finally {
 *   secureWipe(privateKey, masterSecret);
 * }
 * ```
 */
export function secureWipe(
	...data: Array<Uint8Array | null | undefined>
): void {
	for (const item of data) {
		if (item) {
			item.fill(0);
		}
	}

	// Trigger garbage collection if available (requires --expose-gc flag)
	if (typeof globalThis.gc === "function") {
		globalThis.gc();
	}
}

/**
 * Securely clears an object's properties from memory.
 *
 * Useful for clearing decrypted data objects that contain sensitive information.
 *
 * @param obj - Object to clear properties from
 * @example
 * ```typescript
 * const decryptedData = await decrypt();
 * try {
 *   const { bytes, encoding } = decryptedData;
 *   return process(bytes, encoding);
 * } finally {
 *   secureClearObject(decryptedData);
 * }
 * ```
 */
export function secureClearObject(
	obj: Record<string, unknown> | null | undefined,
): void {
	if (obj && typeof obj === "object") {
		for (const key of Object.keys(obj)) {
			obj[key] = null;
		}
	}

	if (typeof globalThis.gc === "function") {
		globalThis.gc();
	}
}
